<template>
  <Popover v-slot="{open}" class="relative">
    <PopoverButton
      class="btn block min-w-[8rem]"
      :class="[open ? 'blue transition-all ease-out duration-150' : 'white', props.class ?? '']"
      ref="buttonRef"
    >
      {{ open ? t('Hide Legend') : t('Show Legend') }}
    </PopoverButton>

    <TransitionRoot
      appear
      :show="open"
      as="template"
      enter="transition-opacity duration-100 ease-out"
      enter-from="opacity-0"
      enter-to="opacity-100"
      leave="transition-opacity duration-150 ease-in"
      leave-from="opacity-100"
      leave-to="opacity-0"
    >
      <PopoverPanel class="absolute z-50" :style="floatingStyles" ref="panelRef">
        <div class="card pb-5 pt-6 pl-6 pr-10 z-[100] flex flex-col gap-1.5">
          <p class="flex items-center gap-2 whitespace-nowrap">
            <s-tooltip wrapperClass="flex items-center">
              <template #tooltip>{{ t('color.green') }}</template>
              <span class="inline-block rounded-full shadow-card w-4 h-4 bg-green-500"></span>
            </s-tooltip>
            <span>{{ t('correct') }}</span>
          </p>
          <p class="flex items-center gap-2 whitespace-nowrap">
            <s-tooltip wrapperClass="flex items-center">
              <template #tooltip>{{ t('color.blue') }}</template>
              <span class="inline-block rounded-full shadow-card w-4 h-4 bg-blue-400"></span>
            </s-tooltip>
            <span>{{ t('partiallyCorrect') }}</span>
          </p>
          <p class="flex items-center gap-2 whitespace-nowrap">
            <s-tooltip wrapperClass="flex items-center">
              <template #tooltip>{{ t('color.red') }}</template>
              <span class="inline-block rounded-full shadow-card w-4 h-4 bg-red-400"></span>
            </s-tooltip>
            <span>{{ t('incorrect') }}</span>
          </p>
          <p class="flex items-center gap-2 whitespace-nowrap">
            <s-tooltip wrapperClass="flex items-center">
              <template #tooltip>{{ t('color.darkGray') }}</template>
              <span class="inline-block rounded-full shadow-card w-4 h-4 bg-gray-600"></span>
            </s-tooltip>
            <span>{{ t('ungraded') }}</span>
          </p>
          <p class="flex items-center gap-2 whitespace-nowrap">
            <s-tooltip wrapperClass="flex items-center">
              <template #tooltip>{{ t('color.gray') }}</template>
              <span class="inline-block rounded-full shadow-card w-4 h-4 bg-gray-400"></span>
            </s-tooltip>
            <span>
              {{ t('draft') }}
              <em>({{ t('ungraded') }})</em>
            </span>
          </p>
          <p class="flex items-center gap-2 whitespace-nowrap">
            <s-tooltip wrapperClass="flex items-center">
              <template #tooltip>{{ t('pencilIcon') }}</template>
              <s-icon name="pencil" class="opacity-70 -mx-0.5" size="20" />
            </s-tooltip>
            <span>{{ t('containsDraft') }}</span>
          </p>
          <p class="flex items-center gap-2 whitespace-nowrap">
            <s-tooltip wrapperClass="flex items-center">
              <template #tooltip>{{ t('color.lightGray') }}</template>
              <span class="inline-block rounded-full shadow-card w-4 h-4 bg-gray-150"></span>
            </s-tooltip>
            <span>{{ t('incomplete') }}</span>
          </p>
        </div>
      </PopoverPanel>
    </TransitionRoot>
  </Popover>
</template>

<script setup lang="ts">
import {TransitionRoot} from '@headlessui/vue';
import {defineProps, ref} from 'vue';
import {Popover, PopoverButton, PopoverPanel} from '@headlessui/vue';
import {useFloating, autoUpdate, offset, shift, flip} from '@floating-ui/vue';
import SIcon from '../../../design-system/SIcon.vue';
import {useI18n} from 'vue-i18n';
import STooltip from '../../../design-system/STooltip.vue';

const props = defineProps<{
  class?: string;
}>();

const {t} = useI18n({
  useScope: 'local',
  inheritLocale: true,
});

const buttonRef = ref<HTMLElement | null>(null);
const panelRef = ref<HTMLElement | null>(null);
const middleware = ref([offset(8), shift(), flip()]);

const {floatingStyles} = useFloating(buttonRef, panelRef, {
  placement: 'bottom-end',
  middleware,
  whileElementsMounted: autoUpdate,
});
</script>
<i18n>
{
  "en": {
    "color": {
      "green": "Green",
      "blue": "Blue",
      "red": "Red",
      "darkGray": "Dark Gray",
      "gray": "Gray",
      "lightGray": "Light Gray",
    },
    "pencilIcon": "Pencil Icon",
  },
  "fr": {
    "color": {
      "green": "Vert",
      "blue": "Bleu",
      "red": "Rouge",
      "darkGray": "Gris foncé",
      "gray": "Gris",
      "lightGray": "Gris clair",
    },
    "pencilIcon": "Icône de crayon",
  }
}
</i18n>
